import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Trophy, Mail, Lock, User, ArrowLeft } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLanguage } from "../context/LanguageContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import LanguageSelector from "../components/LanguageSelector";

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, signup, loginWithGoogle } = useAuth();
  const { t } = useLanguage();

  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const from = location.state?.from || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      if (mode === "login") {
        await login(email.trim(), password);
        toast.success(t("auth.signIn"));
      } else {
        await signup(email.trim(), password, name.trim());
        toast.success(t("auth.register"));
      }
      navigate(from, { replace: true });
    } catch (err) {
      toast.error(err.message || "Authentication failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen hero-gradient flex flex-col">
      <header className="border-b border-white/10 bg-black/30 backdrop-blur-sm">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => navigate("/")}
            data-testid="auth-back-btn"
            className="flex items-center gap-3 text-white hover:opacity-80 transition-opacity"
          >
            <div className="w-10 h-10 bg-neon-cyan/20 rounded flex items-center justify-center">
              <Trophy className="w-6 h-6 text-neon-cyan" />
            </div>
            <h1 className="font-heading text-2xl font-bold tracking-tight uppercase">{t("appName")}</h1>
          </button>
          <LanguageSelector />
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md card-surface p-8" data-testid="auth-card">
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-1 text-white/40 hover:text-white/70 text-sm mb-6 transition-colors"
            data-testid="auth-home-link"
          >
            <ArrowLeft className="w-4 h-4" /> {t("back")}
          </button>

          <h2 className="font-heading text-3xl font-black uppercase tracking-tight text-white mb-1">
            {mode === "login" ? t("auth.loginTitle") : t("auth.registerTitle")}
          </h2>
          <p className="text-white/50 text-sm mb-6">
            {mode === "login" ? t("auth.loginSubtitle") : t("auth.registerSubtitle")}
          </p>

          {/* Google */}
          <Button
            type="button"
            data-testid="google-signin-btn"
            onClick={loginWithGoogle}
            className="w-full bg-white hover:bg-white/90 text-black font-heading uppercase tracking-widest py-4 mb-5 flex items-center justify-center gap-2"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
            {t("auth.continueWithGoogle")}
          </Button>

          <div className="flex items-center gap-3 mb-5">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/30 text-xs uppercase tracking-widest">{t("auth.or")}</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Email/password */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === "register" && (
              <div>
                <Label className="text-white/70 text-xs uppercase tracking-widest">{t("auth.name")}</Label>
                <div className="relative mt-1">
                  <User className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input
                    data-testid="auth-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("auth.namePlaceholder")}
                    className="input-dark pl-9"
                  />
                </div>
              </div>
            )}
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-widest">{t("auth.email")}</Label>
              <div className="relative mt-1">
                <Mail className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="auth-email-input"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t("auth.emailPlaceholder")}
                  className="input-dark pl-9"
                />
              </div>
            </div>
            <div>
              <Label className="text-white/70 text-xs uppercase tracking-widest">{t("auth.password")}</Label>
              <div className="relative mt-1">
                <Lock className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  data-testid="auth-password-input"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t("auth.passwordPlaceholder")}
                  className="input-dark pl-9"
                />
              </div>
            </div>

            <Button
              type="submit"
              data-testid="auth-submit-btn"
              disabled={submitting}
              className="w-full btn-skewed bg-neon-cyan hover:bg-neon-cyan/90 text-black font-heading font-bold uppercase tracking-widest py-4 hover:shadow-neon transition-all duration-300 disabled:opacity-60"
            >
              {submitting ? t("auth.loggingIn") : mode === "login" ? t("auth.login") : t("auth.register")}
            </Button>
          </form>

          <div className="text-center mt-6 text-sm text-white/50">
            {mode === "login" ? t("auth.noAccount") : t("auth.haveAccount")}{" "}
            <button
              type="button"
              data-testid="auth-toggle-mode-btn"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="text-neon-cyan hover:underline font-semibold"
            >
              {mode === "login" ? t("auth.signUp") : t("auth.signIn")}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
